'use client';

import React from 'react';

import { ArrowLeft } from 'lucide-react';

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { Trans } from '@kit/ui/trans';

import { formatPhoneNumber } from '../../schemas/phone-auth.schema';
import { NativeAuthButton } from './native-auth-button';

export function NativeOtpScreen({
  phone,
  email,
  loading,
  resendCooldown,
  primaryColor,
  secondaryColor,
  appBackgroundColor = '#FFFFFF',
  appTextColor = '#1C1C1C',
  onVerify,
  onResend,
  onBack,
}: {
  phone?: string;
  email?: string;
  loading: boolean;
  resendCooldown: number;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  onVerify: (token: string) => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const [token, setToken] = React.useState('');
  const isDark = isDarkBg(appBackgroundColor);

  const handleComplete = (value: string) => {
    setToken(value);
    if (value.length === 6 && !loading) {
      onVerify(value);
    }
  };

  const slotClass = isDark
    ? 'h-12 w-12 border-white/20 bg-white/8 text-lg text-white'
    : 'h-12 w-12 border-gray-200 bg-gray-50 text-lg text-gray-900';

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        disabled={loading}
        className="flex min-h-[44px] items-center gap-2 self-start text-sm font-medium active:opacity-70"
        style={{ color: appTextColor }}
      >
        <ArrowLeft className="h-4 w-4" />
        <Trans i18nKey="auth:back" defaults="Back" />
      </button>

      {/* Message */}
      <div className="text-center">
        <p
          className="text-base font-medium"
          style={{ color: appTextColor }}
        >
          {email ? (
            <Trans
              i18nKey="auth:otpSentToEmail"
              defaults="We sent a verification code to <strong>{{email}}</strong>"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          ) : (
            <Trans
              i18nKey="auth:phoneOtpSentToPhone"
              values={{ phone: formatPhoneNumber(phone ?? '') }}
            />
          )}
        </p>
        <p className="mt-2 text-sm" style={{ color: `${appTextColor}80` }}>
          {email ? (
            <Trans i18nKey="common:otp.enterCodeFromEmail" />
          ) : (
            <Trans i18nKey="auth:enterCodeFromPhone" />
          )}
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          autoComplete="one-time-code"
          value={token}
          onChange={handleComplete}
          disabled={loading}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} className={slotClass} />
            <InputOTPSlot index={1} className={slotClass} />
            <InputOTPSlot index={2} className={slotClass} />
          </InputOTPGroup>
          <InputOTPSeparator style={{ color: `${appTextColor}30` }} />
          <InputOTPGroup>
            <InputOTPSlot index={3} className={slotClass} />
            <InputOTPSlot index={4} className={slotClass} />
            <InputOTPSlot index={5} className={slotClass} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {/* Verify button */}
      <div className="w-full space-y-3">
        <NativeAuthButton
          type="button"
          onClick={() => onVerify(token)}
          disabled={token.length < 6}
          loading={loading}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appTextColor={appTextColor}
        >
          <Trans i18nKey="auth:verifyCode" />
        </NativeAuthButton>

        <NativeAuthButton
          type="button"
          variant="ghost"
          disabled={resendCooldown > 0}
          onClick={onResend}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appTextColor={appTextColor}
        >
          {resendCooldown > 0 ? (
            <Trans
              i18nKey="auth:resendCodeIn"
              values={{ seconds: resendCooldown }}
            />
          ) : (
            <Trans i18nKey="auth:requestNewCode" />
          )}
        </NativeAuthButton>
      </div>
    </div>
  );
}

function isDarkBg(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
